import { useQuery } from "@tanstack/react-query";
import { useParams } from "react-router-dom";

import { SmartCell, unwrapSmartTableEntityPayload } from "./types";

import { ENDPOINTS } from "@/api/application-api";
import { useAuth } from "@/context/auth-context";
import { handleResponse } from "@/utils/errorResponseHandlers";
import { jsonAuthHeaders } from "@/utils/headers";

export interface VirtualCellCoordinates {
  columnId: string;
  rowIndex: number;
}

export interface VersionCellCoordinates extends VirtualCellCoordinates {
  versionId: string;
}

export interface SmartTableCellQueryTarget {
  cellId: string | undefined;
  virtualCell?: VirtualCellCoordinates;
  versionCell?: VersionCellCoordinates;
}

export type FullCellCoordinates = Pick<SmartTableCellQueryTarget, "virtualCell" | "versionCell">;

export const getSmartTableCellQueryKey = (
  wid: number,
  tableId: string | undefined,
  sheetId: string | undefined,
  target: SmartTableCellQueryTarget,
) => {
  const { cellId, virtualCell, versionCell } = target;
  let targetKind = "physical";
  if (versionCell) {
    targetKind = "version";
  } else if (virtualCell) {
    targetKind = "virtual";
  }
  return [
    "smart-tables",
    wid,
    tableId,
    sheetId,
    "cells",
    cellId,
    targetKind,
    versionCell?.versionId,
    versionCell?.columnId ?? virtualCell?.columnId,
    versionCell?.rowIndex ?? virtualCell?.rowIndex,
  ] as const;
};

export const getSmartTableCellQueryKeyPrefix = (
  wid: number,
  tableId: string | undefined,
  sheetId: string | undefined,
  cellId: string | undefined,
) => ["smart-tables", wid, tableId, sheetId, "cells", cellId] as const;

export const fetchSmartTableCell = async ({
  wid,
  tableId,
  sheetId,
  cellId,
  userToken,
  virtualCell,
  versionCell,
}: {
  wid: number;
  tableId: string;
  sheetId: string;
  cellId?: string;
  userToken: string;
  virtualCell?: VirtualCellCoordinates;
  versionCell?: VersionCellCoordinates;
}): Promise<SmartCell> => {
  let endpoint: string;
  if (versionCell) {
    endpoint = ENDPOINTS.smart_table_version_cell(wid, tableId, sheetId, versionCell);
  } else if (virtualCell) {
    endpoint = ENDPOINTS.smart_table_virtual_cell(wid, tableId, sheetId, virtualCell);
  } else {
    endpoint = ENDPOINTS.smart_table_cell(wid, tableId, sheetId, cellId!);
  }
  const response = await fetch(endpoint, {
    headers: jsonAuthHeaders(userToken),
  });
  const payload = await handleResponse(response);
  return unwrapSmartTableEntityPayload<SmartCell>(payload, "cell");
};

export const useGetCell = (
  tableId?: string,
  sheetId?: string,
  cellId?: string,
  coordinates?: FullCellCoordinates,
) => {
  const { virtualCell, versionCell } = coordinates ?? {};
  const userToken = useAuth()?.userToken ?? "";
  const { workspaceId } = useParams<{ workspaceId: string }>();
  const wid = Number(workspaceId);
  const isEnabled = Boolean(
    workspaceId && tableId && sheetId && (cellId || virtualCell || versionCell),
  );
  const query = useQuery<SmartCell>({
    queryKey: getSmartTableCellQueryKey(wid, tableId, sheetId, {
      cellId,
      virtualCell,
      versionCell,
    }),
    queryFn: () =>
      fetchSmartTableCell({
        wid,
        tableId: tableId!,
        sheetId: sheetId!,
        cellId: cellId!,
        userToken,
        virtualCell,
        versionCell,
      }),
    enabled: isEnabled,
  });

  return { ...query, isEnabled };
};
